// Utility functions moved from CourseListComponent

/**
 * Opens the course filter dialog by setting the flag to true.
 */
export function openCourseFilterDialog(context: { showCourseFilterDialog: boolean }) {
    context.showCourseFilterDialog = true;
}

/**
 * Closes the course filter dialog by setting the flag to false.
 */
export function closeCourseFilterDialog(context: { showCourseFilterDialog: boolean }) {
    context.showCourseFilterDialog = false;
}

/**
 * Placeholder for filter cleared logic.
 */
export function onFilterCleared() {
    // Optionally reload courses or reset filters here
}

/**
 * Opens the filter dialog by setting the flag to true.
 */
export function openFilterDialog(context: { showFilterDialog: boolean }) {
    context.showFilterDialog = true;
}

/**
 * Closes the filter dialog by setting the flag to false.
 */
export function closeFilterDialog(context: { showFilterDialog: boolean }) {
    context.showFilterDialog = false;
}

/**
 * Utility to build CourseFilter object from filter event
 */
export function pbuildCourseFilterFromEvent(event: any[], activeTab: string, parentTab: string): any {
    const filter = event.reduce((filter: any, section: any) => { // NOSONAR
        if (section.category === 'Type' && Array.isArray(section.selected) && section.selected.length > 0) {
            filter.type = section.selected;
        }
        if (section.category === 'Status' && Array.isArray(section.selected) && section.selected.length > 0) {
            filter.status = section.selected.map((s: string) => s.toUpperCase());
        }
        if (section.category === 'Published' && Array.isArray(section.selected) && section.selected.length > 0) {
            filter.isPublished = section.selected;
        }
        if (section.category === 'Feedback Form' && Array.isArray(section.selected) && section.selected.length > 0) {
            filter.isFeedbackForm = section.selected;
        }
        if (section.category === 'Duration') {
            if (section.startDate) {
                filter.courseStartDate = formatDate(section.startDate);
            }
            if (section.endDate) {
                filter.courseEndDate = formatDate(section.endDate);
            }
        }
        if (section.category === 'Course Name' && Array.isArray(section.selected) && section.selected.length > 0) {
            filter.courseIds = section.selected;
        }
        if (section.category === 'Course Rating') {
            if (section.min !== undefined && section.min !== '') {
                filter.courseRatingMin = section.min;
            }
            if (section.max !== undefined && section.max !== '') {
                filter.courseRatingMax = section.max;
            }
        }
        if (section.category === 'No of Reviews') {
            if (section.min !== undefined && section.min !== '') {
                filter.reviewCountMin = section.min;
            }
            if (section.max !== undefined && section.max !== '') {
                filter.reviewCountMax = section.max;
            }
        }
        if (section.category === 'Total Capacity') {
            if (section.min !== undefined && section.min !== '') {
                filter.minEnrollmentCount = section.min;
            }
            if (section.max !== undefined && section.max !== '') {
                filter.maxEnrollmentCount = section.max;
            }
        }
        if (section.category === 'Registered Count') {
            if (section.min !== undefined && section.min !== '') {
                filter.minRegisteredCount = section.min;
            }
            if (section.max !== undefined && section.max !== '') {
                filter.maxRegisteredCount = section.max;
            }
        }
        if (section.category === 'Co-faculty' && Array.isArray(section.selected) && section.selected.length > 0) {
            filter.facultyIds = section.selected;
        }
        // Registration Detail Filters
        if (section.category === 'Fee Category') {
            // Exclude 'All' and only add if not empty
            const feeCategories = (section.selected || []).filter((v: string) => v !== 'All');
            if (feeCategories.length > 0) {
                filter.feeCategories = feeCategories;
            }
        }
        if (section.category === 'Highest Educational Experience') {
            if (section.selected && section.selected.length > 0) {
                filter.qualifications = section.selected;
            }
        }
        if (section.category === 'Work Experience') {
            if (section.selected && section.selected.length > 0) {
                // Convert labels like '1-2 years' to '1,2' and '20+ years' to '20,50'
                filter.workExpRanges = section.selected.map((label: string) => {
                    const rangeRegex = /^(\d+)-(\d+) years$/;
                    const plusRegex = /^(\d+)\+ years$/;
                    // ReDoS-safe: \d+ is anchored by literal '-(\d+) years' / '\+ years' with no nested or overlapping quantifiers.
                    const match = rangeRegex.exec(label); //NOSONAR
                    if (match) {
                        return `${match[1]},${match[2]}`;
                    }
                    const plusMatch = plusRegex.exec(label); //NOSONAR
                    if (plusMatch) {
                        return `${plusMatch[1]},50`;
                    }
                    return label;
                });
            }
        }
        // Certificate Filters
        if (section.category === 'Certificate Type' && Array.isArray(section.selected) && section.selected.length > 0) {
            // Use API format values directly - support multiple selections
            filter.certificateTypes = section.selected;
        }
        if (
            section.category === 'Physical Certificate' &&
            Array.isArray(section.selected) &&
            section.selected.length > 0
        ) {
            // Pass "Yes"/"No" values directly to API without conversion
            filter.isIssuedPhysically = section.selected;
        }
        // Add more categories as needed
        return filter;
    }, {} as any);
    // After reduce, check if status is missing/null/empty and set from activeTab
    if (!filter.status || (Array.isArray(filter.status) && filter.status.length === 0)) {
        if (activeTab) {
            filter.status = [activeTab.toUpperCase()];
        }
    }
    // Set self and isParent based on parentTab
    if (parentTab === 'my-courses') {
        filter.self = true;
        filter.isParent = false;
    } else if (parentTab === 'programs') {
        filter.self = true;
        filter.isParent = true;
    } else if (parentTab === 'approval-courses') {
        filter.self = false;
        filter.isParent = false;
    } else if (parentTab === 'approval-programs') {
        filter.self = false;
        filter.isParent = true;
    }
    // Add any extra checks for filter values here if needed
    return filter;
}

/**
 * Simplifies the filter event for easier processing.
 */
export function simplifyFilterEvent(event: any[]): any[] {
    return event.map((section: any) => {  // NOSONAR
        if (section.category === 'Duration') {
            const startDateField = section.fields.find((f: any) => f.key === 'startDate');
            const endDateField = section.fields.find((f: any) => f.key === 'endDate');
            return {
                category: section.category,
                startDate: startDateField?.value || '',
                endDate: endDateField?.value || '',
            };
        } else if (section.category === 'Course Rating' || section.category === 'No of Reviews') {
            const rangeField = section.fields.find((f: any) => f.type === 'range');
            // Get the default min/max from the field definition
            const defaultMin = rangeField?.min;
            const defaultMax = rangeField?.max;
            const userMin = rangeField?.value?.min;
            const userMax = rangeField?.value?.max;
            // Only send values if user changed them from default
            const min = userMin !== undefined && userMin !== defaultMin ? userMin : '';
            const max = userMax !== undefined && userMax !== defaultMax ? userMax : '';
            return {
                category: section.category,
                min,
                max,
            };
        } else if (section.category === 'Course Name') {
            const multiselectField = section.fields.find((f: any) => f.type === 'multiselect');
            // Map selected course names to ids using the global mapping
            const nameToIdMap = (globalThis as any).__courseNameToIdMap || {};
            const selectedNames = multiselectField?.selected || [];
            const selectedIds = selectedNames.map((name: string) => nameToIdMap[name] || name);
            return {
                category: section.category,
                selected: selectedIds,
            };
        } else if (section.category === 'Total Capacity' || section.category === 'Registered Count') {  // NOSONAR
            const rangeField = section.fields.find((f: any) => f.type === 'range');
            const defaultMin = rangeField?.min;
            const defaultMax = rangeField?.max;
            const userMin = rangeField?.value?.min;
            const userMax = rangeField?.value?.max;
            const min = userMin !== undefined && userMin !== defaultMin ? userMin : '';
            const max = userMax !== undefined && userMax !== defaultMax ? userMax : '';
            return {
                category: section.category,
                min,
                max,
            };
        } else {
            return {
                category: section.category,
                selected: section.fields[0]?.selected || [],
            };
        }
    });
}

/**
 * Formats a date string or Date object to 'yyyy-mm-dd'.
 */
export function formatDate(date: string | Date): string {
    if (!date) return '';
    const d = typeof date === 'string' ? new Date(date) : date;
    if (Number.isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * Formats a Date object to LocalDateTime string format (yyyy-MM-ddTHH:mm:ss)
 * Used for API requests that require LocalDateTime format without timezone
 * @param date - The Date object to format
 * @returns Formatted date string in LocalDateTime format (e.g., "2026-04-10T09:00:00")
 */
export function formatToLocalDateTime(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

/**
 * Utility class to manage filter/search state for any component.
 * Usage:
 *   const filterState = new FilterState();
 *   filterState.setFilterActive(true);
 *   filterState.setSearchText('abc');
 *   filterState.isActive(); // returns true if filter or search is active
 */
export class FilterState {
    private _filterActive = false;
    private _searchText = '';

    setFilterActive(active: boolean) {
        this._filterActive = active;
    }

    setSearchText(text: string) {
        this._searchText = text;
    }

    clear() {
        this._filterActive = false;
        this._searchText = '';
    }

    isActive(): boolean {
        return this._filterActive || !!this._searchText;
    }
}
